import React from "react";
import { Award, Factory, Users, BadgeCheck } from "lucide-react";

// "People fixing air compressors" imagery
// Primary: technician in PPE actively servicing industrial pumps + electrical panels
const TECH_IMG_A =
  "https://images.unsplash.com/photo-1709804250377-27ce2d57209e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHx0ZWNobmljaWFuJTIwcmVwYWlyaW5nJTIwaW5kdXN0cmlhbCUyMGVxdWlwbWVudHxlbnwwfHx8fDE3NzgxMjUxNDF8MA&ixlib=rb-4.1.0&q=85&w=1200";
// Secondary: hands-on technician in blue overalls operating industrial machinery
const TECH_IMG_B =
  "https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&w=1200&q=85";

const POINTS = [
  {
    icon: Award,
    title: "Certified Millwrights",
    text: "Trade-qualified millwrights, factory-trained on every major compressor brand.",
  },
  {
    icon: Factory,
    title: "Industry-tested",
    text: "Manufacturing, food & beverage, automotive, pharma, energy — we’ve serviced them all.",
  },
  {
    icon: Users,
    title: "Honest pricing",
    text: "Detailed quotes, no surprise charges, parts at fair markup.",
  },
  {
    icon: BadgeCheck,
    title: "Workmanship guarantee",
    text: "Every repair is backed by our 90-day no-fail warranty.",
  },
];

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* IMAGE BENTO — perfectly aligned 6 col × 6 row grid */}
        <div className="lg:col-span-6 grid grid-cols-6 grid-rows-6 gap-3 h-[520px] md:h-[560px]">
          {/* Tech A — left top, large */}
          <figure
            data-testid="about-photo-a"
            className="col-span-3 row-span-4 overflow-hidden border border-slate-200 bg-slate-100 group"
          >
            <img
              src={TECH_IMG_A}
              alt="Pneupro millwright servicing an industrial air compressor"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </figure>

          {/* Tech B — right top */}
          <figure
            data-testid="about-photo-b"
            className="col-span-3 row-span-4 overflow-hidden border border-slate-200 bg-slate-100 group"
          >
            <img
              src={TECH_IMG_B}
              alt="Pneupro technician on-site repairing a compressor unit"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </figure>

          {/* HQ card — left bottom */}
          <div
            data-testid="about-hq-card"
            className="col-span-3 row-span-2 bg-blue-600 text-white p-5 flex flex-col justify-center"
          >
            <div className="text-[11px] font-bold tracking-widest uppercase opacity-90">
              Headquarters
            </div>
            <div className="font-heading text-2xl md:text-3xl font-black leading-tight mt-1">
              Surrey, BC
            </div>
            <div className="text-xs text-white/80 mt-1">Serving all of British Columbia</div>
          </div>

          {/* Service zone card — right bottom */}
          <div
            data-testid="about-service-zone-card"
            className="col-span-3 row-span-2 bg-slate-900 text-white p-5 flex flex-col justify-center"
          >
            <div className="text-[11px] font-bold tracking-widest uppercase text-blue-400">
              Service zone
            </div>
            <div className="font-heading text-2xl md:text-3xl font-black leading-tight mt-1">
              Across BC's Lower Mainland
            </div>
            <div className="text-xs text-slate-400 mt-1">After-hours service available</div>
          </div>
        </div>

        {/* TEXT COLUMN */}
        <div className="lg:col-span-6 lg:pl-8">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-600">
            About Pneupro
          </div>
          <h2 className="font-heading mt-3 text-4xl md:text-5xl font-black tracking-tighter uppercase text-slate-900">
            Industrial air,
            <span className="block text-blue-600">professionally serviced.</span>
            <span className="block text-slate-500 text-2xl md:text-3xl mt-3 normal-case tracking-tight font-bold">
              Built in Surrey. Run for British Columbia.
            </span>
          </h2>

          <div className="mt-8 space-y-4 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              Pneupro Air Compressor Services Ltd. is a full-service partner for industrial
              air compressor systems across British Columbia. Our trade-qualified millwrights
              service rotary screw, reciprocating, and centrifugal compressors — from one-off
              repairs to structured maintenance programs and after-hours emergency response.
            </p>
            <p>
              Every job follows the same playbook: a written quote, OEM parts and OEM procedures,
              a 90-day no-fail warranty, and an invoice that matches what we promised. Whether
              you run a single shop unit or a multi-site enterprise, you get the same standard
              of work — every visit, every time.
            </p>
            <p>
              We exist because operators kept telling us what they actually wanted from a service
              partner: someone who picks up the phone, shows up on time, and stands behind the
              repair.
            </p>
          </div>
          <p className="mt-6 text-base text-slate-500 italic">
            That's the pitch. The rest is execution.
          </p>
        </div>

        {/* Full-width 4-across pillars row */}
        <div className="lg:col-span-12 mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {POINTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`about-point-${i}`}
                className="border border-slate-200 bg-white p-6 h-full transition-all hover:-translate-y-0.5 hover:border-blue-600"
              >
                <Icon className="h-5 w-5 text-blue-600" />
                <div className="font-heading mt-3 font-bold text-slate-900">{p.title}</div>
                <div className="mt-1 text-sm text-slate-600 leading-relaxed">{p.text}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
